// src/pages/admin/JournalEntry.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Card,
  DataStateHandler,
  ConfirmModal,
  Button,
  CheckboxField,
  Loading,
  showSuccess,
  showError,
  useScrollRestore,
  usePersistedState,
  SyncNetsuiteButton,
} from "@common";
import {
  journalEntryColumns,
  isOrganizerOrAdmin,
  canViewFinance,
  canEditFinance,
  buildJournalEntryItems,
  buildJournalEntryPayload,
  buildIntercompanyJournalEntryPayload,
  buildIntercompanyClinicTotals,
  validateJournalEntryData,
  calculateDdsDraw,
  torontoToday,
} from "@utils";
import { DEDUCTION_COLUMN_KEYS, isApprovalApproved } from "@utils/associateFee";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import PractitionerQuickLookModal from "../../common/modal/PractitionerQuickLookModal";
import JournalEntryPreview from "./JournalEntryPreview";
import IntercompanyJournalEntryPreview from "./IntercompanyJournalEntryPreview";
import api from "../../api/api";
import { adminEndpoints } from "../../api/endpoints";
import {
  useMonthlyCompensations,
  useUpdatePractitionerByClinic,
  useUpdateMonthlyCompensationForClinic,
  useBulkApproveMonthlyCompensation,
  useCreateNetsuiteJournalEntry,
  useCreateNetsuiteIntercompanyJournalEntry,
  useSearchNetsuiteJournalEntries,
  useSearchNetsuiteAdvIntercompanyJournalEntries,
  useClinics,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import ColumnToggle from "../../common/ui/ColumnToggle";
import ClinicJournalGroup from "./ClinicJournalGroup";

// Format "YYYY-MM" -> "March 2026" for NetSuite memo search.
function formatMonthYearLong(month) {
  if (!month || typeof month !== "string") return "";
  const parts = month.split("-");
  if (parts.length < 2) return "";
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!y || !m) return "";
  const date = new Date(y, m - 1, 1);
  const monthName = date.toLocaleString("en-US", { month: "long" });
  return `${monthName} ${y}`;
}

function buildJournalMemo(month) {
  const label = formatMonthYearLong(month);
  if (!label) return "";
  return `Production, Collections and Associate Fee ${label}`;
}

export default function JournalEntry({
  months,
  clinics,
  month: legacyMonth,
  clinic: legacyClinic,
}) {
  // Normalize array props (the page now passes arrays; keep legacy single
  // props for back-compat).
  const selectedMonths = useMemo(() => {
    if (Array.isArray(months) && months.length) return months;
    return legacyMonth ? [legacyMonth] : [];
  }, [months, legacyMonth]);
  const selectedClinics = useMemo(() => {
    if (Array.isArray(clinics) && clinics.length) return clinics;
    return legacyClinic ? [legacyClinic] : [];
  }, [clinics, legacyClinic]);
  const clinicFilter = useMemo(() => selectedClinics.join(","), [selectedClinics]);
  // Most NetSuite search/memo logic is single-month. When multiple months
  // are selected we use the first one for the existing-entry search; the
  // backend list endpoint receives the joined string so all selected
  // months come back together.
  const month = selectedMonths[0] || "";
  const monthsParam = useMemo(() => selectedMonths.join(","), [selectedMonths]);
  // Default Journal Entry trandate = last calendar day of the selected
  // payroll month (e.g. month="2026-04" -> "2026-04-30"). Falls back to
  // today when no month is selected.
  const defaultJournalTrandate = useMemo(() => {
    if (!month || typeof month !== "string") {
      return torontoToday();
    }
    const [y, m] = month.split("-").map((v) => parseInt(v, 10));
    if (!y || !m) return torontoToday();
    const lastDay = new Date(y, m, 0).getDate();
    return `${month}-${String(lastDay).padStart(2, "0")}`;
  }, [month]);
  const { user } = useAuth();
  const isAdminOrOrg = isOrganizerOrAdmin(user);
  const canView = canViewFinance(user); // Org, Admin, AM, Observer, Payroll, Bookkeeper, Finance Coord
  const canEdit = canEditFinance(user); // Org, Admin, Payroll, Finance Coord

  // Lookup: clinic code -> netsuite_subsidiary_id. `/clinics/` list is gated by
  // IsAdminOrganizerOrPayroll, whose read side is can_view_payroll_finance — the
  // same set as `canView`. This map drives the created/available status of every
  // clinic group and the create-JE payload, so gating it any narrower leaves
  // writers (Payroll, Finance Coordinator) with every clinic stuck on "missing".
  const clinicsQuery = useClinics(
    {},
    { enabled: canView, refetchInterval: false, refetchIntervalInBackground: false },
  );
  const subsidiaryByClinic = useMemo(() => {
    const list = clinicsQuery.data?.results ?? clinicsQuery.data ?? [];
    const map = {};
    for (const c of list) {
      if (c && c.clinic) map[c.clinic] = c.netsuite_subsidiary_id ?? null;
    }
    return map;
  }, [clinicsQuery.data]);

  const journalMemo = useMemo(() => buildJournalMemo(month), [month]);

  // Memo substring used when searching for *existing* NetSuite journal
  // entries. The backend combines this with `month` to OR-match common
  // formats ("March 2026", "Mar 2026", "03-2026", "3/2026", "2026-03", ...).
  const journalSearchMemo = "Production, Collections and Associate Fee";

  // Batch-search existing NetSuite journal entries for all clinic subsidiaries in one call.
  const subsidiaryIds = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const sid of Object.values(subsidiaryByClinic)) {
      if (sid === null || sid === undefined || sid === "") continue;
      if (seen.has(sid)) continue;
      seen.add(sid);
      out.push(sid);
    }
    return out;
  }, [subsidiaryByClinic]);

  const journalSearchQuery = useSearchNetsuiteJournalEntries(
    {
      subsidiaries: subsidiaryIds,
      memo: journalSearchMemo,
      month,
    },
    { refetchInterval: false, refetchIntervalInBackground: false },
  );

  // Group existing journal entries by subsidiary id for per-clinic lookup
  const entriesBySubsidiary = useMemo(() => {
    const data = journalSearchQuery.data;
    const items = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const map = {};
    if (!Array.isArray(items)) return map;
    for (const it of items) {
      const sid = it?.subsidiary_id ?? it?.subsidiaryid ?? null;
      if (sid === null || sid === undefined) continue;
      const key = String(sid);
      if (!map[key]) map[key] = [];
      map[key].push(it);
    }
    return map;
  }, [journalSearchQuery.data]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalPractitioner, setModalPractitioner] = useState(null);
  const [modalOpenInEdit, setModalOpenInEdit] = useState(true);
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("journalEntry_hiddenColumns");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Deduction columns (purple) are hidden by default and grouped under a
  // single "Total Fee" column. The Total Fee header has a +/- toggle.
  const [deductionsExpanded, setDeductionsExpanded] = useState(() => {
    try {
      return localStorage.getItem("journalEntry_deductionsExpanded") === "true";
    } catch {
      return false;
    }
  });
  const toggleDeductions = useCallback(() => {
    setDeductionsExpanded((v) => {
      const next = !v;
      try {
        localStorage.setItem("journalEntry_deductionsExpanded", String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Approval state
  const [approveConfirmRow, setApproveConfirmRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Journal entry preview state: { clinic, clinicRows, payload } | null
  const [journalPreview, setJournalPreview] = useState(null);

  // Intercompany JE preview state: { payload, monthLabel, totalDraw } | null
  const [intercompanyPreview, setIntercompanyPreview] = useState(null);

  // Clinics we've just successfully created a journal entry for in this
  // session. Used to flip the "Create Journal Entry" button to a
  // "Done!!" label without triggering a slow NetSuite refetch.
  const [justCreatedClinics, setJustCreatedClinics] = useState(() => new Set());

  const queryClient = useQueryClient();
  const updateMut = useUpdatePractitionerByClinic();
  const approveMut = useUpdateMonthlyCompensationForClinic();
  const bulkApproveMut = useBulkApproveMonthlyCompensation();

  // Fetch all data in a single call (no limit/offset) — include both DDS and HYG
  const { data, isLoading, error } = useMonthlyCompensations(
    {
      month: monthsParam,
      ...(clinicFilter ? { clinic: clinicFilter } : {}),
    },
    {
      // No clinic filter means the backend returns EVERY clinic's rows, so an
      // empty selection must not fire the request at all.
      enabled: selectedMonths.length > 0 && canView && !!clinicFilter,
      refetchInterval: false,
      refetchIntervalInBackground: false,
    },
  );

  // See Check.jsx: a disabled useQuery still serves cached data for the same
  // key (Cash Flow uses it too), so gate on canView rather than `enabled` alone.
  const rows = useMemo(() => (canView ? (data?.results ?? data ?? []) : []), [data, canView]);

  // Preserve & restore window scroll across unmount/remount (tab switch).
  useScrollRestore(`journalEntry:scroll:${month || ""}:${clinicFilter || ""}`, !isLoading);

  // Sort rows: clinic ASC, then DDS before HYG (then anything else), then practitioner name
  const sortedRows = useMemo(() => {
    const typeRank = (t) => {
      const up = (t || "").toString().toUpperCase();
      if (up === "DDS") return 0;
      if (up === "HYG") return 1;
      return 2;
    };
    return [...rows].sort((a, b) => {
      const clinicCmp = (a.clinic || "").localeCompare(b.clinic || "");
      if (clinicCmp !== 0) return clinicCmp;
      const typeCmp = typeRank(a.practitioner_type) - typeRank(b.practitioner_type);
      if (typeCmp !== 0) return typeCmp;
      return (a.practitioner_name || "").localeCompare(b.practitioner_name || "");
    });
  }, [rows]);

  // Group sorted rows by clinic (preserves sort order)
  const clinicGroups = useMemo(() => {
    const groups = new Map();
    for (const r of sortedRows) {
      const key = r.clinic || "—";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    }
    return Array.from(groups.entries()); // [ [clinic, rows[]], ... ]
  }, [sortedRows]);

  // Filter clinics by journal entry status: "all" | "available" | "missing" | "created"
  // - missing:   no journal entry yet
  // - available: missing AND all practitioners approved (ready to create)
  // - created:   journal entry already exists in NetSuite
  const [statusFilter, setStatusFilter] = usePersistedState("journalEntry:statusFilter", "all");

  const isClinicEligibleToCreate = useCallback(
    (clinic, clinicRows) => {
      if (!clinicRows.every((r) => isApprovalApproved(r.admin_approval))) return false;
      const sid = subsidiaryByClinic[clinic] ?? null;
      return validateJournalEntryData({ subsidiaryId: sid, clinicRows }).ok;
    },
    [subsidiaryByClinic],
  );

  const clinicStatusCounts = useMemo(() => {
    let missing = 0;
    let created = 0;
    let available = 0;
    for (const [clinic, clinicRows] of clinicGroups) {
      const sid = subsidiaryByClinic[clinic] ?? null;
      const existing =
        sid !== null && sid !== undefined ? (entriesBySubsidiary[String(sid)] ?? []) : [];
      if (existing.length > 0 || justCreatedClinics.has(clinic)) {
        created += 1;
      } else if (isClinicEligibleToCreate(clinic, clinicRows)) {
        available += 1;
      } else {
        missing += 1;
      }
    }
    return { all: clinicGroups.length, available, missing, created };
  }, [
    clinicGroups,
    subsidiaryByClinic,
    entriesBySubsidiary,
    isClinicEligibleToCreate,
    justCreatedClinics,
  ]);

  const filteredClinicGroups = useMemo(() => {
    if (statusFilter === "all") return clinicGroups;
    return clinicGroups.filter(([clinic, clinicRows]) => {
      const sid = subsidiaryByClinic[clinic] ?? null;
      const existing =
        sid !== null && sid !== undefined ? (entriesBySubsidiary[String(sid)] ?? []) : [];
      const hasExisting = existing.length > 0 || justCreatedClinics.has(clinic);
      if (statusFilter === "created") return hasExisting;
      if (hasExisting) return false;
      const eligible = isClinicEligibleToCreate(clinic, clinicRows);
      if (statusFilter === "available") return eligible;
      if (statusFilter === "missing") return !eligible;
      return true;
    });
  }, [
    clinicGroups,
    subsidiaryByClinic,
    entriesBySubsidiary,
    statusFilter,
    isClinicEligibleToCreate,
    justCreatedClinics,
  ]);

  const createJournalMut = useCreateNetsuiteJournalEntry();
  const createIntercompanyMut = useCreateNetsuiteIntercompanyJournalEntry();

  // Look up an existing intercompany journal entry for this month, so the
  // "Create Intercompany Journal Entry" button can be swapped for a link.
  const intercompanyMemoBase = "Associate Fee";
  const intercompanySearch = useSearchNetsuiteAdvIntercompanyJournalEntries(
    {
      memo: intercompanyMemoBase,
      month,
      subsidiary: canEdit ? 1 : undefined, // SFC header subsidiary
    },
    { refetchInterval: false, refetchIntervalInBackground: false },
  );
  const existingIntercompanyEntry = useMemo(() => {
    const data = intercompanySearch.data;
    const items = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    if (!Array.isArray(items) || items.length === 0) return null;
    return items[0];
  }, [intercompanySearch.data]);

  // Build + validate the payload, then open the preview modal.
  const handleCreateJournalEntry = useCallback(
    (clinic, clinicRows) => {
      // Hard guard at the entry point: every row must be admin-approved.
      // (The button is also disabled when this fails; this is a safety net
      // for any code path that might bypass the disabled state.)
      const unapproved = clinicRows.filter((r) => !isApprovalApproved(r.admin_approval));
      if (unapproved.length > 0) {
        showError(
          `Cannot create journal entry for ${clinic}: ${unapproved.length} practitioner${
            unapproved.length === 1 ? " is" : "s are"
          } not yet admin-approved.`,
        );
        return;
      }
      try {
        const payload = buildJournalEntryPayload({
          memo: journalMemo,
          subsidiaryId: subsidiaryByClinic[clinic] ?? null,
          trandate: defaultJournalTrandate,
          clinicRows,
        });
        setJournalPreview({ clinic, clinicRows, payload });
      } catch (err) {
        showError(getApiErrorMessage(err));
      }
    },
    [journalMemo, subsidiaryByClinic, defaultJournalTrandate],
  );

  // Actually send the payload to NetSuite after user confirms.
  const confirmCreateJournalEntry = useCallback(async () => {
    if (!journalPreview) return;
    try {
      await createJournalMut.mutateAsync(journalPreview.payload);
      setJustCreatedClinics((prev) => {
        const next = new Set(prev);
        next.add(journalPreview.clinic);
        return next;
      });
      showSuccess(`Journal entry created for ${journalPreview.clinic}`);
      setJournalPreview(null);
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }, [createJournalMut, journalPreview]);

  // ── Intercompany Journal Entry ──
  // Eligible only after every clinic in this month has a NetSuite journal
  // entry (either pre-existing or just created in this session).
  const allClinicsHaveJournalEntry = useMemo(() => {
    if (clinicGroups.length === 0) return false;
    for (const [clinic, clinicRows] of clinicGroups) {
      // A $0 clinic has no lines to post, so it never gets its own journal
      // entry — it must not block the intercompany one.
      if (buildJournalEntryItems(clinicRows).length === 0) continue;
      const sid = subsidiaryByClinic[clinic] ?? null;
      const existing =
        sid !== null && sid !== undefined ? (entriesBySubsidiary[String(sid)] ?? []) : [];
      if (existing.length === 0 && !justCreatedClinics.has(clinic)) return false;
    }
    return true;
  }, [clinicGroups, subsidiaryByClinic, entriesBySubsidiary, justCreatedClinics]);

  const intercompanyClinicTotals = useMemo(
    () => buildIntercompanyClinicTotals(clinicGroups, subsidiaryByClinic),
    [clinicGroups, subsidiaryByClinic],
  );

  const handleCreateIntercompanyJournalEntry = useCallback(() => {
    try {
      const payload = buildIntercompanyJournalEntryPayload({
        month,
        clinicGroups,
        subsidiaryByClinic,
        trandate: defaultJournalTrandate,
      });
      const totalDraw = intercompanyClinicTotals.reduce((s, c) => s + c.ddsDraw, 0);
      setIntercompanyPreview({
        payload,
        monthLabel: formatMonthYearLong(month),
        totalDraw,
      });
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }, [month, clinicGroups, subsidiaryByClinic, intercompanyClinicTotals, defaultJournalTrandate]);

  const confirmCreateIntercompanyJournalEntry = useCallback(async () => {
    if (!intercompanyPreview) return;
    try {
      await createIntercompanyMut.mutateAsync(intercompanyPreview.payload);
      showSuccess("Intercompany journal entry created");
      setIntercompanyPreview(null);
      intercompanySearch.refetch?.();
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }, [createIntercompanyMut, intercompanyPreview, intercompanySearch]);

  // ── Export filtered clinic groups to CSV ──
  // When filter is "missing", restrict export to HYG practitioners only.
  const handleExportCsv = useCallback(() => {
    let flat = filteredClinicGroups.flatMap(([, clinicRows]) => clinicRows);
    if (statusFilter === "missing") {
      flat = flat.filter((r) => String(r.practitioner_type || "").toUpperCase() === "HYG");
    }
    if (flat.length === 0) {
      showError("No rows to export.");
      return;
    }
    const csvEscape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = [
      "Clinic",
      "Practitioner",
      "Type",
      "DDS Draw",
      "Production",
      "Collection",
      "Paid Based On",
      "Associate Fee %",
      "Lab Cost",
      "Material Cost",
      "Invisalign Cost",
      "Anesthesia Fee",
      "Anesthesiologist Fee",
      "Facility Fee",
      "Botox",
      "Production Adjustment",
      "Other Profit",
      "Other Deduction",
      "Approved",
      "NetSuite Subsidiary ID",
    ];
    const lines = [headers.map(csvEscape).join(",")];
    for (const r of flat) {
      const otherProfit = Array.isArray(r.other_profit)
        ? r.other_profit.reduce((s, t) => s + (Number(t?.profit) || 0), 0)
        : 0;
      const otherDeduction = Array.isArray(r.other_deduction)
        ? r.other_deduction.reduce((s, t) => {
            const explicit = Number(t?.amount);
            if (Number.isFinite(explicit) && explicit !== 0) return s + explicit;
            if (t?.hour_rate !== undefined || t?.hours !== undefined) {
              return s + (Number(t?.hour_rate) || 0) * (Number(t?.hours) || 0);
            }
            return s + (Number(t?.profit) || 0);
          }, 0)
        : 0;
      const sid = subsidiaryByClinic[r.clinic] ?? "";
      lines.push(
        [
          r.clinic,
          r.practitioner_name,
          r.practitioner_type,
          calculateDdsDraw(r),
          r.production?.total, // ?.total — serializer returns nested { total, breakdown }
          r.collection?.total,
          r.paid_based_on,
          r.associate_fee_percentage,
          r.lab_cost?.total,
          r.material_cost?.total,
          r.invisalign_cost?.total,
          r.anesthesia_fee?.total,
          r.anesthesiologist_fee?.total,
          r.facility_fee?.total,
          r.botox?.total,
          r.production_adjustment?.total,
          otherProfit,
          otherDeduction,
          isApprovalApproved(r.admin_approval) ? "Yes" : "No",
          sid,
        ]
          .map(csvEscape)
          .join(","),
      );
    }
    const blob = new Blob(["\uFEFF" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `journal-entry-${statusFilter}-${month || "all"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showSuccess(`Exported ${flat.length} row${flat.length !== 1 ? "s" : ""}.`);
  }, [filteredClinicGroups, subsidiaryByClinic, statusFilter, month]);

  // Open modal — fetch full practitioner profile
  const openPractitionerModal = useCallback(async (row, { inEdit }) => {
    setModalPractitioner({
      id: row.practitioner_id,
      name: row.practitioner_name,
      practitioner_type: row.practitioner_type,
      email: row.practitioner_email,
      associate_fee_percentage: row.associate_fee_percentage,
      clinic: row.clinic,
    });
    setModalOpenInEdit(inEdit);
    setModalOpen(true);
    try {
      const res = await api.get(
        adminEndpoints.GET_PRACTITIONER_BY_CLINIC(row.clinic, row.practitioner_id),
      );
      setModalPractitioner(res.data);
    } catch {
      // keep partial data if fetch fails
    }
  }, []);

  const handleUpdateClick = useCallback(
    (row) => openPractitionerModal(row, { inEdit: true }),
    [openPractitionerModal],
  );
  const handlePractitionerNameClick = useCallback(
    (row) => openPractitionerModal(row, { inEdit: false }),
    [openPractitionerModal],
  );

  async function handleSavePractitioner(original, payload) {
    await updateMut.mutateAsync({
      clinicCode: original.clinic,
      id: original.id,
      data: payload,
      method: "patch",
    });
    queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
  }

  // ── Single approval ──
  const handleApproveClick = useCallback((row) => {
    setApproveConfirmRow(row);
  }, []);

  const confirmApprove = useCallback(async () => {
    if (!approveConfirmRow) return;
    try {
      await approveMut.mutateAsync({
        clinicCode: approveConfirmRow.clinic,
        id: approveConfirmRow.id,
        data: {
          admin_approval: {
            approved: true,
            user: user?.email || "",
          },
        },
      });
      showSuccess(`Approved ${approveConfirmRow.practitioner_name}`);
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setApproveConfirmRow(null);
    }
  }, [approveConfirmRow, approveMut, user?.email]);

  // ── Bulk approval (admin only) ──
  const unapprovedRows = useMemo(
    () => rows.filter((r) => !isApprovalApproved(r.admin_approval)),
    [rows],
  );

  const toggleSelectRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allUnapprovedIds = unapprovedRows.map((r) => r.id);
      const allSelected =
        allUnapprovedIds.length > 0 && allUnapprovedIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allUnapprovedIds);
    });
  }, [unapprovedRows]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [month, clinicFilter]);

  const selectedUnapproved = useMemo(
    () => rows.filter((r) => !isApprovalApproved(r.admin_approval) && selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const handleBulkApproveClick = useCallback(() => {
    if (selectedUnapproved.length === 0) {
      showError("No unapproved rows selected.");
      return;
    }
    setBulkApproveOpen(true);
  }, [selectedUnapproved]);

  const handleApproveAllClick = useCallback(() => {
    if (unapprovedRows.length === 0) {
      showError("All records are already approved.");
      return;
    }
    setSelectedIds(new Set(unapprovedRows.map((r) => r.id)));
    setBulkApproveOpen(true);
  }, [unapprovedRows]);

  const confirmBulkApprove = useCallback(async () => {
    setBulkApproving(true);
    try {
      const ids = selectedUnapproved.map((r) => r.id);
      await bulkApproveMut.mutateAsync({ ids, approved: true });
      showSuccess(`All ${ids.length} records approved!`);
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setBulkApproving(false);
      setBulkApproveOpen(false);
      setSelectedIds(new Set());
    }
  }, [selectedUnapproved, bulkApproveMut]);

  const allColumns = useMemo(
    () =>
      journalEntryColumns(canEdit ? handleUpdateClick : null, {
        onApprove: canEdit ? handleApproveClick : null,
        onPractitionerClick: handlePractitionerNameClick,
        deductionsExpanded,
        onToggleDeductions: toggleDeductions,
      }),
    [
      canEdit,
      handleUpdateClick,
      handleApproveClick,
      handlePractitionerNameClick,
      deductionsExpanded,
      toggleDeductions,
    ],
  );

  const columns = useMemo(
    () =>
      allColumns.filter((c) => {
        if (hiddenColumns.has(c.key)) return false;
        if (!deductionsExpanded && DEDUCTION_COLUMN_KEYS.includes(c.key)) return false;
        return true;
      }),
    [allColumns, hiddenColumns, deductionsExpanded],
  );

  const toggleColumn = useCallback((key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem("journalEntry_hiddenColumns", JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Prepend selection checkbox column (admins only)
  const columnsWithSelect = useMemo(() => {
    if (!isAdminOrOrg || unapprovedRows.length === 0) return columns;
    const allUnapprovedIds = unapprovedRows.map((r) => r.id);
    const allSelected =
      allUnapprovedIds.length > 0 && allUnapprovedIds.every((id) => selectedIds.has(id));
    return [
      {
        key: "_select",
        header: <CheckboxField checked={allSelected} onChange={toggleSelectAll} />,
        width: 40,
        render: (r) =>
          isApprovalApproved(r.admin_approval) ? null : (
            <CheckboxField checked={selectedIds.has(r.id)} onChange={() => toggleSelectRow(r.id)} />
          ),
      },
      ...columns,
    ];
  }, [columns, unapprovedRows, selectedIds, toggleSelectAll, toggleSelectRow, isAdminOrOrg]);

  return (
    <div className="w-full overflow-x-hidden">
      {approveConfirmRow && (
        <ConfirmModal
          isOpen={!!approveConfirmRow}
          onClose={() => setApproveConfirmRow(null)}
          onConfirm={confirmApprove}
          title="Approve Record"
          message={`Approve associate fee record for ${approveConfirmRow.practitioner_name} (${approveConfirmRow.clinic})?`}
          confirmText="Approve"
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={approveMut.isPending}
        />
      )}
      {bulkApproveOpen && (
        <ConfirmModal
          isOpen={bulkApproveOpen}
          onClose={() => !bulkApproving && setBulkApproveOpen(false)}
          onConfirm={confirmBulkApprove}
          title="Approve Selected Records"
          message={
            <div>
              <p className="text-sm mb-2">
                Approve <strong>{selectedUnapproved.length}</strong> record
                {selectedUnapproved.length !== 1 ? "s" : ""}?
              </p>
              <div className="max-h-48 overflow-y-auto rounded border border-palette-gray-divider bg-palette-gray-soft p-2">
                {selectedUnapproved.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-1 text-sm">
                    <span className="font-medium">{r.practitioner_name}</span>
                    <span className="text-palette-gray-secondary">{r.clinic}</span>
                  </div>
                ))}
              </div>
            </div>
          }
          confirmText={bulkApproving ? `Approving...` : `Approve ${selectedUnapproved.length}`}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={bulkApproving}
        />
      )}
      {journalPreview && (
        <ConfirmModal
          isOpen={!!journalPreview}
          onClose={() => !createJournalMut.isPending && setJournalPreview(null)}
          onConfirm={confirmCreateJournalEntry}
          title={`Create NetSuite Journal Entry — ${journalPreview.clinic}`}
          size="xl"
          confirmText={createJournalMut.isPending ? "Sending..." : "Confirm & Send"}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={createJournalMut.isPending}
          message={
            <JournalEntryPreview
              preview={journalPreview}
              onChange={(patch) =>
                setJournalPreview((prev) =>
                  prev ? { ...prev, payload: { ...prev.payload, ...patch } } : prev,
                )
              }
            />
          }
        />
      )}
      {intercompanyPreview && (
        <ConfirmModal
          isOpen={!!intercompanyPreview}
          onClose={() => !createIntercompanyMut.isPending && setIntercompanyPreview(null)}
          onConfirm={confirmCreateIntercompanyJournalEntry}
          title="Create Intercompany Journal Entry"
          size="xl"
          confirmText={createIntercompanyMut.isPending ? "Sending..." : "Confirm & Send"}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={createIntercompanyMut.isPending}
          message={
            <IntercompanyJournalEntryPreview
              preview={intercompanyPreview}
              onChange={(patch) =>
                setIntercompanyPreview((prev) =>
                  prev ? { ...prev, payload: { ...prev.payload, ...patch } } : prev,
                )
              }
            />
          }
        />
      )}
      {modalOpen && (
        <PractitionerQuickLookModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalPractitioner(null);
          }}
          practitioner={modalPractitioner}
          canEdit={canEdit}
          openInEdit={modalOpenInEdit && canEdit}
          onSave={canEdit ? handleSavePractitioner : null}
        />
      )}
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold">Journal Entry</h3>
            <ColumnToggle
              allColumns={allColumns}
              hiddenColumns={hiddenColumns}
              onChange={toggleColumn}
            />
            {isAdminOrOrg && <SyncNetsuiteButton source="journal entry tab" />}
            <div
              className="inline-flex rounded-md border border-palette-gray-border overflow-hidden"
              role="group"
              aria-label="Filter clinics by journal entry status"
            >
              {[
                { key: "all", label: `All (${clinicStatusCounts.all})` },
                { key: "available", label: `Available (${clinicStatusCounts.available})` },
                { key: "missing", label: `Missing (${clinicStatusCounts.missing})` },
                { key: "created", label: `Created (${clinicStatusCounts.created})` },
              ].map((opt) => {
                const active = statusFilter === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setStatusFilter(opt.key)}
                    className={`px-3 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-palette-gray-border ${
                      active
                        ? "bg-palette-indigo-badge text-white"
                        : "bg-white text-palette-gray-heading hover:bg-palette-gray-soft"
                    }`}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={isLoading || filteredClinicGroups.length === 0}
            >
              Export CSV
            </Button>
            {selectedUnapproved.length > 0 && isAdminOrOrg && (
              <Button variant="outline" size="sm" onClick={handleBulkApproveClick}>
                Approve Selected ({selectedUnapproved.length})
              </Button>
            )}
            {isAdminOrOrg && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleApproveAllClick}
                disabled={isLoading || unapprovedRows.length === 0}
              >
                Approve All
              </Button>
            )}
            {canEdit &&
              (existingIntercompanyEntry ? (
                <a
                  href={
                    existingIntercompanyEntry.link ||
                    `https://4571901.app.netsuite.com/app/accounting/transactions/icjournal.nl?id=${existingIntercompanyEntry.id}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-palette-emerald-soft text-palette-emerald-hover border border-palette-emerald-border hover:bg-palette-emerald-muted"
                  title="Open existing intercompany journal entry in NetSuite"
                >
                  View Intercompany Journal Entry
                </a>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreateIntercompanyJournalEntry}
                  disabled={
                    isLoading ||
                    journalSearchQuery.isFetching ||
                    intercompanySearch.isFetching ||
                    !allClinicsHaveJournalEntry ||
                    intercompanyClinicTotals.length === 0 ||
                    createIntercompanyMut.isPending
                  }
                  title={
                    !allClinicsHaveJournalEntry
                      ? "All clinics with amounts must have a journal entry created first."
                      : intercompanyClinicTotals.length === 0
                        ? "No clinics with a non-zero DDS draw."
                        : "Create Intercompany Journal Entry"
                  }
                >
                  Create Intercompany Journal Entry
                </Button>
              ))}
          </div>
        </div>

        <Card bodyClassName="p-0">
          <DataStateHandler
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && rows.length === 0}
            loadingMessage="Loading journal entries..."
            emptyMessage={
              clinicFilter
                ? "No data for this month"
                : "Select one or more clinics to view journal entries."
            }
            showLoadingSpinner={true}
          >
            <div className="relative max-w-full overflow-x-auto">
              {journalSearchQuery.isFetching && (
                <div className="absolute inset-0 z-10 flex items-start justify-center pt-6 bg-white/70 backdrop-blur-sm pointer-events-none">
                  <Loading text="Checking NetSuite for existing journal entries..." size="lg" />
                </div>
              )}
              <div className="min-w-max space-y-8 p-4">
                {filteredClinicGroups.length === 0 ? (
                  <div className="text-sm text-palette-gray-secondary italic px-2 py-6 text-center">
                    No clinics match the selected filter.
                  </div>
                ) : (
                  filteredClinicGroups.map(([clinic, clinicRows]) => {
                    const sid = subsidiaryByClinic[clinic] ?? null;
                    const entries =
                      sid !== null && sid !== undefined
                        ? (entriesBySubsidiary[String(sid)] ?? [])
                        : [];
                    return (
                      <ClinicJournalGroup
                        key={clinic}
                        clinic={clinic}
                        clinicRows={clinicRows}
                        existingEntries={entries}
                        justCreated={justCreatedClinics.has(clinic)}
                        isSearching={journalSearchQuery.isLoading}
                        columnsWithSelect={columnsWithSelect}
                        onCreateJournalEntry={canEdit ? handleCreateJournalEntry : null}
                        isCreating={createJournalMut.isPending}
                        canEdit={canEdit}
                      />
                    );
                  })
                )}
              </div>
            </div>
          </DataStateHandler>
        </Card>
      </Card>
    </div>
  );
}
